import debounce from 'lodash/debounce';
import { ComponentController, type IComponentControllerScope, type IItem, type ICollectionParams, type IEventEmitter } from '../../system';
import { GridComponent, StretchGridStrategy } from '../listing-grid-v1';
import { type IListingGridColumn } from '../../listing/IListingGridColumn';
import { ItemsGridStrategy, HeightGridStrategy, CommonGridStrategy } from '../listing-grid-v1/strategies';
import { type IDialogManager } from '../../popup';
import { GridComponentEvents } from '../listing-grid-v1/grid.component';

export interface IPageListingGridScope extends IComponentControllerScope {
    onGridReady(api): void;
    onGridColumnsReady?(grid): void;
}

export interface IPageListingGridV1BasisComponent extends IEventEmitter {
    getListing(): Promise<GridComponent>;
    load(debounce?: boolean): void;
}

export abstract class PageListingGridV1BasisComponent extends ComponentController implements IPageListingGridV1BasisComponent {
    protected dialogManager: IDialogManager;
    protected listing: GridComponent;
    protected listingDeferred = {
        resolve: null,
        reject: null
    };
    protected listingReady = new Promise<GridComponent>((resolve, reject) => {
        this.listingDeferred.resolve = resolve;
        this.listingDeferred.reject = reject;
    });
    private doDebounceUpdate;
    private cancelPaginationTitlesRequest = null;

    protected constructor(
        protected $scope: IPageListingGridScope,
        protected $injector: angular.auto.IInjectorService
    ) {
        super($scope);
        this.dialogManager = $injector.get<IDialogManager>('dialogManager');
        this.beforeInit();
    }

    public getListing(): Promise<GridComponent> {
        return this.listingReady.then(listing => listing);
    }

    public load(debounce = false): void {
        this.listingReady
            .then(listing => {
                listing.activate();
                listing.updateFilters();
                listing.loadDataResource(debounce);
            })
            .catch(e => {
                this.dialogManager.error(e);
            });
    }

    protected abstract getColumns(): IListingGridColumn[];

    protected abstract setGridStrategy(listing: GridComponent): void;

    protected abstract setGridName(listing: GridComponent): void;

    protected setGridItemsTotalName(listing: GridComponent) {
        listing.setTotalName('');
    }

    protected onGridColumnsReady(listing: GridComponent) {
        if (this.$scope.onGridColumnsReady) this.$scope.onGridColumnsReady({ grid: listing });
    }

    protected registerGridSelection(_listing: GridComponent) {}

    protected registerGridFilters(_listing: GridComponent) {}

    protected setGridDataProvider(_listing: GridComponent) {}

    protected changePageSize(listing: GridComponent, pageSize: number) {
        listing.setStrategy(new CommonGridStrategy(new ItemsGridStrategy(pageSize)));
    }

    protected changeHeight(listing: GridComponent, height: number, stretch: boolean = false) {
        listing.setStrategy(new CommonGridStrategy(new HeightGridStrategy(height, stretch)));
    }

    protected changeStretchHeight(listing: GridComponent, element: HTMLElement, rowWeight = 1, rowHeight?: number) {
        listing.setStrategy(new CommonGridStrategy(new StretchGridStrategy(element, rowWeight, rowHeight)));
    }

    protected setGridColumns(listing: GridComponent) {
        listing.setColumns(this.getColumns());
    }

    protected onGridReady(result) {
        this.init(result.api);
    }

    protected setGridActions(_listing: GridComponent): void {}

    protected get gridItems(): Array<IItem> {
        return (this.listing && this.listing.getItems()) || null;
    }

    protected get requestParams(): ICollectionParams {
        return (this.listing && this.listing && this.listing.getRequestParams()) || null;
    }

    protected setPageNo(no: number) {
        this.getListing().then(listing => {
            listing.setPage(no);
        });
    }

    /**
     * Show loading process (set loading flag = true)
     */
    protected activateGridLoading() {
        this.getListing().then(listing => {
            listing.loading(true);
        });
    }

    /**
     * Hide loading process (set loading flag = false)
     */
    protected inactivateGridLoading() {
        this.getListing().then(listing => {
            listing.loading(false);
        });
    }

    protected setGridItems(items) {
        this.getListing().then(listing => {
            listing.setData(items);
        });
    }

    protected onFilterUpdate() {
        if (!this.doDebounceUpdate) {
            this.doDebounceUpdate = debounce(() => {
                this.getListing().then(async listing => {
                    if (listing.getFilterStrategy()) {
                        await listing.filterStrategyUpdate();
                    }
                    listing.getActiveFilters();
                    listing.updateFilters();
                    listing.goToPage(0);
                });
            }, 300);
        }
        this.doDebounceUpdate();
    }

    private init(listing: GridComponent) {
        try {
            this.setGridStrategy(listing);
            this.setGridColumns(listing);
            this.setGridActions(listing);
            this.onGridColumnsReady(listing);
            this.registerGridFilters(listing);
            this.registerGridSelection(listing);
            this.setGridName(listing);
            this.setGridItemsTotalName(listing);
            this.setGridDataProvider(listing);
            this.listing = listing;
            this.listingDeferred.resolve(listing);
            listing.on(GridComponentEvents.Loaded, () => {
                this.setPaginationTitles(listing);
            });
        } catch (error) {
            this.listingDeferred.reject(error);
        }
    }

    private setPaginationTitles(listing: GridComponent) {
        const $timeout = this.$injector.get('$timeout'),
            dataProvider = listing.getDataProvider(),
            api = listing.getPaginationApi();

        if (!api) {
            return;
        }

        $timeout().then(() => {
            const request = listing.getRequestParams();
            request.filters.firstPage = api.getFirstPageNo();
            request.filters.lastPage = api.getLastPageNo();

            this.loadPaginationTitles(request, dataProvider)
                .catch(error => {
                    if (dataProvider.isError(error, 'IndexOutOfBoundsException')) {
                        request.filters.lastPage = api.getLastPageNo() - 1;
                        return this.loadPaginationTitles(request, dataProvider);
                    }
                    return null;
                })
                .then(names => {
                    listing.setPaginationTitles(names);
                });
        });
    }

    private loadPaginationTitles(request, dataProvider) {
        if (this.cancelPaginationTitlesRequest) {
            this.cancelPaginationTitlesRequest();
        }
        const cancelPromise = new Promise<void>(resolve => {
            this.cancelPaginationTitlesRequest = () => {
                resolve();
            };
        });

        return dataProvider.getPageNames(request, {}, cancelPromise);
    }

    private beforeInit() {
        this.$scope.onGridReady = this.onGridReady.bind(this);
    }
}
